export const securityPageStyles = `
@font-face {
  font-family: "GTF Good Mono";
  src: url("/fonts/GoodMono/GTFGoodMonoTRIAL-Thin.otf") format("opentype");
  font-weight: 100;
  font-display: swap;
}

.zmk-security-root {
  --zmk-bg: var(--zmk-palette-bg);
  --zmk-bg-deep: var(--zmk-palette-deep-bg);
  --zmk-panel: var(--zmk-product-card-bg);
  --zmk-card: var(--zmk-palette-card);
  --zmk-ink: var(--zmk-palette-ink);
  --zmk-copy: color-mix(in srgb, var(--zmk-ink) 68%, transparent);
  --zmk-muted: color-mix(in srgb, var(--zmk-ink) 52%, transparent);
  --zmk-faint: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  --zmk-line: color-mix(in srgb, var(--zmk-ink) 12%, transparent);
  --zmk-line-soft: color-mix(in srgb, var(--zmk-ink) 7%, transparent);
  --zmk-blue: var(--zmk-palette-blue);
  --zmk-blue-hover: var(--zmk-palette-blue-hover);
  --zmk-ease: cubic-bezier(0.16, 1, 0.3, 1);

  background: var(--zmk-bg);
  color: var(--zmk-ink);
  color-scheme: dark;
  font-family: var(--font-brand-stack);
  font-weight: 300;
  line-height: normal;
  min-height: 100svh;
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
}

.zmk-security-root *,
.zmk-security-root *::before,
.zmk-security-root *::after {
  box-sizing: border-box;
}

.zmk-security-root a {
  color: inherit;
  text-decoration: none;
}

.zmk-security-root button,
.zmk-security-root input,
.zmk-security-root textarea,
.zmk-security-root select {
  font-family: var(--font-brand-stack);
}

.zmk-security-root ::selection {
  background: color-mix(in srgb, var(--zmk-blue) 35%, transparent);
  color: var(--zmk-ink);
}

.zmk-sec-progress {
  background: var(--zmk-blue);
  height: 2px;
  left: 0;
  position: fixed;
  top: 0;
  transform: scaleX(0);
  transform-origin: left center;
  width: 100%;
  will-change: transform;
  z-index: 1002;
}

.zmk-sec-nav {
  background: transparent;
  border-bottom: 1px solid transparent;
  inset-inline: 0;
  position: fixed;
  top: 0;
  transform: translateY(0);
  transition:
    background 0.3s ease,
    border-color 0.3s ease,
    transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1000;
}

/* Reference values: scrim past 80px, retract on downward scroll. */
.zmk-sec-nav.is-scrolled {
  background: color-mix(in srgb, var(--zmk-bg) 94%, transparent);
  border-bottom-color: color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
}

.zmk-sec-nav.is-hidden {
  transform: translateY(-100%);
}

.zmk-sec-nav__inner {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1440px;
  padding: 20px 48px;
}

.zmk-sec-nav__brand {
  align-items: center;
  display: flex;
}

.zmk-sec-nav__brand img {
  display: block;
  height: 34px;
  object-fit: contain;
  width: auto;
}

.zmk-sec-nav__links {
  align-items: center;
  display: flex;
  gap: 40px;
}

.zmk-sec-nav__links a,
.zmk-sec-nav__request {
  color: var(--zmk-ink) !important;
  font-size: 15px !important;
  font-weight: 400 !important;
  height: auto !important;
  line-height: 1.2;
  padding: 0 !important;
  position: relative;
}

.zmk-sec-nav__links a::after,
.zmk-sec-nav__request::after {
  background: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  bottom: -6px;
  content: "";
  height: 1.5px;
  left: 0;
  position: absolute;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.18s ease;
  width: 100%;
}

.zmk-sec-nav__links a:hover,
.zmk-sec-nav__request:hover {
  background: transparent !important;
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent) !important;
}

.zmk-sec-nav__links a:hover::after,
.zmk-sec-nav__request:hover::after {
  transform: scaleX(1);
}

.zmk-sec-nav__menu {
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 16%, transparent) !important;
  color: var(--zmk-ink) !important;
  display: none !important;
  height: 40px !important;
  width: 40px !important;
}

.zmk-sec-nav__menu svg {
  height: 18px;
  width: 18px;
}

.zmk-sec-mobile-menu {
  background: color-mix(in srgb, var(--zmk-bg-deep) 96%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 8%, transparent);
  display: none;
  flex-direction: column;
  gap: 22px;
  inset: 73px 0 auto;
  padding: 26px 24px 30px;
  position: fixed;
  transform: translateY(-10px);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.24s ease,
    transform 0.24s ease;
  z-index: 999;
}

.zmk-sec-mobile-menu.is-open {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.zmk-sec-mobile-menu a {
  color: color-mix(in srgb, var(--zmk-ink) 86%, transparent);
  font-size: 20px;
}

.zmk-sec-button {
  align-items: center;
  background: var(--zmk-blue) !important;
  border: 0 !important;
  border-radius: 8px !important;
  box-shadow: 0 0 20px 1px color-mix(in srgb, var(--zmk-blue) 13%, transparent);
  color: var(--zmk-ink) !important;
  display: inline-flex;
  font-family: var(--font-brand-stack) !important;
  font-size: 16px !important;
  font-weight: 400 !important;
  gap: 10px;
  min-height: 48px !important;
  padding: 14px 28px !important;
  transition:
    filter 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.2s ease !important;
}

.zmk-sec-button:hover {
  box-shadow: 0 0 16px 1px color-mix(in srgb, var(--zmk-blue) 22%, transparent);
  filter: brightness(1.14);
  transform: translateY(-2px);
}

.zmk-sec-button svg {
  height: 15px;
  width: 15px;
}

.zmk-sec-outline-button {
  background: transparent !important;
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 28%, transparent) !important;
  border-radius: 8px !important;
  color: var(--zmk-ink) !important;
  display: inline-flex !important;
  font-family: var(--font-brand-stack) !important;
  font-weight: 400 !important;
  gap: 10px;
  min-height: 48px !important;
  padding: 14px 24px !important;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    background-color 0.2s ease !important;
}

.zmk-sec-outline-button:hover {
  background: color-mix(in srgb, var(--zmk-ink) 4%, transparent) !important;
  border-color: color-mix(in srgb, var(--zmk-ink) 50%, transparent) !important;
  transform: translateY(-2px);
}

.zmk-sec-main {
  position: relative;
}

.zmk-sec-layout {
  display: grid;
  gap: 72px;
  grid-template-columns: 200px minmax(0, 1fr);
  margin: 0 auto;
  max-width: 1320px;
  padding: 72px 48px 120px;
  position: relative;
  z-index: 1;
}

.zmk-sec-toc {
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: sticky;
  top: 110px;
}

.zmk-sec-toc__title,
.zmk-sec-mono {
  color: var(--zmk-faint);
  font-family: "GTF Good Mono", monospace !important;
  font-size: 10.5px;
  font-weight: 100;
  letter-spacing: 0;
  margin: 0;
  text-transform: uppercase;
}

.zmk-sec-toc__title {
  color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
  font-size: 10px;
  letter-spacing: 0.16em;
  margin-bottom: 14px;
}

/* Mobile-only jump control. Hidden on desktop so the rail stays a plain list. */
.zmk-sec-toc__trigger,
.zmk-sec-toc__backdrop {
  display: none;
}

.zmk-sec-toc__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.zmk-sec-toc__list a {
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-size: 14px;
  font-weight: 300;
  padding: 6px 0;
  transition: color 0.2s ease;
}

.zmk-sec-toc__list a.is-active {
  color: var(--zmk-ink);
}

.zmk-sec-content {
  display: flex;
  flex-direction: column;
  gap: 96px;
  min-width: 0;
}

.zmk-sec-hero {
  background: var(--zmk-bg);
  border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
  min-height: 0;
  position: relative;
  z-index: 1;
}

.zmk-sec-hero__inner {
  margin: 0 auto;
  max-width: 1320px;
  padding: 190px 48px 90px;
}

.zmk-sec-display {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.1;
  margin: 0;
  text-wrap: balance;
}

.zmk-sec-hero h1 {
  font-size: clamp(44px, 5vw, 64px);
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.08;
  max-width: 720px;
}

.zmk-sec-kicker {
  margin-bottom: 28px;
}

.zmk-sec-hero .zmk-sec-kicker {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  font-size: 11px;
  letter-spacing: 0.16em;
  line-height: normal;
  margin-bottom: 36px;
}

.zmk-sec-lede {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 18px;
  font-weight: 300;
  line-height: 1.6;
  margin: 26px 0 0;
  max-width: 600px;
  text-wrap: pretty;
}

.zmk-sec-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 40px;
}

.zmk-sec-hero .zmk-sec-button,
.zmk-sec-hero .zmk-sec-outline-button {
  font-size: 15px !important;
  height: auto !important;
  line-height: normal !important;
  min-height: 0 !important;
  padding: 13px 24px !important;
}

.zmk-sec-hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
}

.zmk-sec-hero-badges span {
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 18%, transparent);
  border-radius: 6px;
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10.5px;
  font-weight: 400;
  letter-spacing: 0.1em;
  line-height: normal;
  padding: 7px 12px;
  text-transform: uppercase;
}

.zmk-sec-overview,
.zmk-sec-domain,
.zmk-sec-regulatory,
.zmk-sec-vdp,
.zmk-sec-faq-section {
  scroll-margin-top: 96px;
}

.zmk-sec-document-kicker {
  color: color-mix(in srgb, var(--zmk-ink) 45%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 11px;
  font-weight: 100;
  letter-spacing: 0.14em;
  line-height: normal;
  margin: 0 0 16px;
  text-transform: uppercase;
}

.zmk-sec-overview .zmk-sec-document-kicker {
  margin-bottom: 24px;
}

.zmk-sec-overview > p {
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent);
  font-size: 17px;
  font-weight: 300;
  line-height: 1.7;
  margin: 0;
  max-width: 640px;
  text-wrap: pretty;
}

.zmk-sec-domain__description,
.zmk-sec-vdp__description {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 16px;
  font-weight: 300;
  line-height: 1.65;
  margin: 0 0 28px;
  max-width: 620px;
  text-wrap: pretty;
}

.zmk-sec-vdp__description {
  margin-bottom: 32px;
  max-width: 660px;
}

.zmk-sec-card-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.zmk-sec-card {
  background: var(--zmk-card);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 7%, transparent);
  border-radius: 10px;
  padding: 28px 30px;
}

.zmk-sec-card__num {
  color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: normal;
}

.zmk-sec-card h3 {
  color: var(--zmk-ink);
  font-family: var(--font-brand-stack);
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  margin: 10px 0 0;
}

.zmk-sec-card p {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 14.5px;
  font-weight: 300;
  line-height: 1.65;
  margin: 10px 0 0;
}

.zmk-sec-governance-trust {
  background: var(--zmk-card);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 7%, transparent);
  border-radius: 10px;
  margin-bottom: 16px;
  padding: 32px 34px;
}

.zmk-sec-governance-trust h3 {
  color: var(--zmk-ink);
  font-size: 17px;
  font-weight: 500;
  margin: 0 0 12px;
}

.zmk-sec-governance-trust p {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 14.5px;
  font-weight: 300;
  line-height: 1.65;
  margin: 0 0 22px;
  max-width: 560px;
  text-wrap: pretty;
}

.zmk-sec-governance-trust .zmk-sec-button {
  font-size: 15px !important;
  height: auto !important;
  line-height: normal !important;
  min-height: 0 !important;
  padding: 13px 24px !important;
}

.zmk-sec-governance-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.zmk-sec-governance-card {
  background: var(--zmk-card);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 7%, transparent);
  border-radius: 10px;
  padding: 26px;
}

.zmk-sec-governance-card .zmk-sec-card__num {
  display: block;
  margin-bottom: 10px;
}

.zmk-sec-governance-card h3 {
  color: var(--zmk-ink);
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 10px;
}

.zmk-sec-governance-card p {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 14px;
  font-weight: 300;
  line-height: 1.6;
  margin: 0;
}

.zmk-sec-governance-group {
  margin-top: 40px;
}

.zmk-sec-governance-group > h3,
.zmk-sec-regulatory__group > h3 {
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent);
  font-size: 15px;
  font-weight: 400;
  margin: 0 0 16px;
}

.zmk-sec-regulatory .zmk-sec-document-kicker,
.zmk-sec-faq-section .zmk-sec-document-kicker {
  margin-bottom: 20px;
}

.zmk-sec-regulatory__rows {
  border-top: 1px solid var(--zmk-line);
  margin: 0;
}

.zmk-sec-regulatory__group + .zmk-sec-regulatory__group {
  margin-top: 40px;
}

.zmk-sec-regulatory__row {
  border-bottom: 1px solid var(--zmk-line);
  display: grid;
  gap: 32px;
  grid-template-columns: 240px minmax(0, 1fr);
  padding: 22px 0;
}

.zmk-sec-regulatory__row dt,
.zmk-sec-regulatory__row dd {
  font-size: 15px;
  margin: 0;
}

.zmk-sec-regulatory__row dt {
  color: var(--zmk-ink);
  font-weight: 500;
  white-space: nowrap;
}

.zmk-sec-regulatory__row dd {
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent);
  font-weight: 300;
  line-height: 1.65;
}

.zmk-sec-vdp-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 16px;
}

.zmk-sec-vdp-grid--scope {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
}

.zmk-sec-vdp > .zmk-sec-vdp-grid:last-child {
  margin-bottom: 0;
}

.zmk-sec-vdp-card {
  background: var(--zmk-card);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 7%, transparent);
  border-radius: 10px;
  padding: 28px 30px;
}

.zmk-sec-vdp-card--wide {
  margin-bottom: 16px;
  padding: 30px 32px;
}

.zmk-sec-vdp-grid:first-of-type .zmk-sec-vdp-card {
  padding: 30px 32px;
}

.zmk-sec-vdp-card h3 {
  color: var(--zmk-ink);
  font-size: 17px;
  font-weight: 500;
  margin: 0 0 12px;
}

.zmk-sec-vdp-card h4 {
  color: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  font-size: 13.5px;
  font-weight: 500;
  margin: 0 0 8px;
}

.zmk-sec-vdp-card h4:not(:first-of-type) {
  margin-top: 18px;
}

.zmk-sec-vdp-card p {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 14.5px;
  font-weight: 300;
  line-height: 1.65;
  margin: 0;
}

.zmk-sec-vdp-card strong {
  color: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  font-weight: 500;
}

.zmk-sec-vdp-card__label {
  color: color-mix(in srgb, var(--zmk-ink) 45%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  font-weight: 100;
  letter-spacing: 0.14em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.zmk-sec-vdp-card__email {
  margin-bottom: 16px;
}

.zmk-sec-text-link {
  align-items: baseline;
  color: var(--zmk-blue-hover) !important;
  display: inline-flex;
  font-size: 15px;
  font-weight: 400;
  gap: 7px;
  text-decoration: underline 1px color-mix(in srgb, var(--zmk-blue-hover) 40%, transparent) !important;
  text-underline-offset: 5px;
}

.zmk-sec-vdp-card__fine {
  font-size: 13.5px !important;
  line-height: 1.6 !important;
}

.zmk-sec-vdp-card__disclaimer {
  color: color-mix(in srgb, var(--zmk-ink) 45%, transparent) !important;
  font-size: 13px !important;
  font-style: italic;
  margin-top: 16px !important;
}

.zmk-sec-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.zmk-sec-list li {
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent);
  display: flex;
  font-size: 14.5px;
  font-weight: 300;
  gap: 12px;
  line-height: 1.6;
}

.zmk-sec-list li::before {
  background: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  border-radius: 999px;
  content: "";
  flex: none;
  height: 6px;
  margin-top: 7px;
  width: 6px;
}

.zmk-sec-vdp-card--rewards .zmk-sec-list {
  gap: 8px;
}

.zmk-sec-vdp-card--rewards .zmk-sec-list li {
  font-size: 14px;
  line-height: 1.5;
}

.zmk-sec-vdp-card--rewards .zmk-sec-list li::before {
  margin-top: 6px;
}

.zmk-sec-response {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.zmk-sec-response__item {
  align-items: baseline;
  display: flex;
  gap: 12px;
}

.zmk-sec-response__step {
  align-items: center;
  background: color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  border-radius: 999px;
  color: var(--zmk-ink);
  display: inline-flex;
  flex: none;
  font-size: 12px;
  font-weight: 500;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.zmk-sec-response__item > span:last-child {
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent);
  font-size: 14.5px;
  font-weight: 300;
  line-height: 1.5;
}

.zmk-sec-faq {
  border-top: 1px solid var(--zmk-line);
}

.zmk-sec-faq__item {
  border-bottom: 1px solid var(--zmk-line);
}

.zmk-sec-faq__toggle {
  align-items: baseline;
  color: var(--zmk-ink);
  cursor: pointer;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  list-style: none;
  padding: 22px 0;
}

.zmk-sec-faq__toggle::-webkit-details-marker {
  display: none;
}

.zmk-sec-faq__toggle > span:first-child {
  color: var(--zmk-ink);
  font-size: 16px;
  font-weight: 400;
  min-width: 0;
  overflow-wrap: anywhere;
}

.zmk-sec-faq__icon {
  color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
  flex: none;
  font-size: 0;
  font-weight: 300;
  line-height: 1;
}

.zmk-sec-faq__icon::before {
  content: "+";
  font-size: 18px;
}

.zmk-sec-faq__item[open] .zmk-sec-faq__icon::before {
  content: "−";
}

.zmk-sec-faq__answer {
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.7;
  margin: 0;
  max-width: 620px;
  padding: 0 0 24px;
}

.zmk-sec-closing {
  border-top: 1px solid var(--zmk-line);
  padding-top: 48px;
}

.zmk-sec-closing > p {
  color: var(--zmk-ink);
  font-size: 17px;
  font-weight: 400;
  line-height: 1.7;
  margin: 0 0 40px;
  max-width: 640px;
  text-wrap: pretty;
}

.zmk-sec-closing .zmk-sec-text-link span {
  font-size: 12px;
}

.zmk-sec-footer {
  background: var(--zmk-palette-footer);
}

.zmk-sec-footer__inner {
  margin: 0 auto;
  max-width: 1440px;
  padding: 80px 48px 40px;
}

.zmk-sec-footer__grid {
  align-items: start;
  display: grid;
  gap: 64px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding-bottom: 64px;
}

.zmk-sec-footer__brand img {
  display: block;
  height: 30px;
  margin-bottom: 20px;
  width: auto;
}

.zmk-sec-footer__brand p {
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-size: 15px;
  line-height: 1.55;
  margin: 0;
  max-width: 360px;
}

.zmk-sec-footer__links {
  display: flex;
  gap: 88px;
}

.zmk-sec-footer__col {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.zmk-sec-footer__col h3 {
  color: color-mix(in srgb, var(--zmk-ink) 38%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  font-weight: 100;
  letter-spacing: 0.14em;
  margin: 0 0 4px;
  text-transform: uppercase;
}

.zmk-sec-footer__col a,
.zmk-sec-footer__col button {
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent) !important;
  font-size: 15px !important;
  font-weight: 300 !important;
  height: auto !important;
  justify-content: flex-start !important;
  padding: 0 !important;
  text-align: left;
}

.zmk-sec-footer__col button:hover {
  background: transparent !important;
}

.zmk-sec-footer__bottom {
  border-top: 1px solid var(--zmk-line);
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  display: flex;
  font-size: 13px;
  font-weight: 300;
  padding-top: 32px;
}

.zmk-security-root [data-sreveal] {
  opacity: 0;
  transform: translate3d(0, 14px, 0);
  transition:
    opacity 0.7s var(--zmk-ease),
    transform 0.7s var(--zmk-ease);
}

.zmk-security-root [data-sreveal].is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.zmk-sec-hero [data-sreveal] {
  transform: translate3d(0, 14px, 0);
}

.zmk-sec-hero h1[data-sreveal] {
  transition-delay: 0.08s;
}

.zmk-sec-hero .zmk-sec-lede[data-sreveal] {
  transition-delay: 0.16s;
}

.zmk-sec-hero .zmk-sec-actions[data-sreveal] {
  transition-delay: 0.22s;
}

.zmk-sec-hero .zmk-sec-hero-badges[data-sreveal] {
  transition-delay: 0.28s;
}

@media (max-width: 1023px) {
  .zmk-sec-hero h1 {
    font-size: clamp(42px, 5.6vw, 58px);
  }

  .zmk-sec-nav__links {
    display: none;
  }

  .zmk-sec-nav__menu {
    display: inline-flex !important;
  }

  .zmk-sec-mobile-menu {
    display: flex;
  }

  .zmk-sec-governance-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .zmk-sec-regulatory__row {
    gap: 20px;
    grid-template-columns: 160px minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .zmk-sec-nav__inner {
    padding: 16px 20px;
  }

  .zmk-sec-nav__brand img {
    height: 28px;
  }

  .zmk-sec-layout {
    gap: 20px;
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 88px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 24px;
  }

  .zmk-sec-toc {
    background: var(--zmk-palette-bg);
    border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
    margin: 0 -24px;
    padding: 0 24px;
    position: sticky;
    top: 58px;
    transition: top 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    width: calc(100% + 48px);
    z-index: 15;
  }

  .zmk-security-root.is-nav-hidden .zmk-sec-toc {
    top: 0;
  }

  .zmk-sec-overview,
  .zmk-sec-domain,
  .zmk-sec-regulatory,
  .zmk-sec-vdp,
  .zmk-sec-faq-section {
    scroll-margin-top: 122px;
  }

  .zmk-security-root.is-nav-hidden .zmk-sec-overview,
  .zmk-security-root.is-nav-hidden .zmk-sec-domain,
  .zmk-security-root.is-nav-hidden .zmk-sec-regulatory,
  .zmk-security-root.is-nav-hidden .zmk-sec-vdp,
  .zmk-security-root.is-nav-hidden .zmk-sec-faq-section {
    scroll-margin-top: 64px;
  }

  .zmk-sec-toc__title {
    display: none;
  }

  .zmk-sec-toc__trigger {
    align-items: center !important;
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    color: var(--zmk-ink) !important;
    display: flex !important;
    gap: 12px !important;
    height: auto !important;
    justify-content: space-between !important;
    min-width: 0;
    padding: 14px 0 !important;
    text-align: left;
    width: 100%;
  }

  .zmk-sec-toc__trigger::before {
    color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
    content: "Contents";
    flex: none;
    font-family: "GTF Good Mono", monospace;
    font-size: 10px;
    font-weight: 100;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .zmk-sec-toc__trigger-label {
    flex: 1 1 auto;
    font-size: 15px;
    font-weight: 400;
    min-width: 0;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .zmk-sec-toc__chevron {
    color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
    flex: none;
    height: 16px;
    transition: transform 0.25s ease;
    width: 16px;
  }

  .zmk-sec-toc.is-open .zmk-sec-toc__chevron {
    transform: rotate(180deg);
  }

  .zmk-sec-toc__backdrop {
    background: color-mix(in srgb, var(--zmk-bg) 55%, transparent);
    border: 0;
    display: block;
    inset: 0;
    position: fixed;
    z-index: 1;
  }

  .zmk-sec-toc__list {
    background: var(--zmk-product-card-bg);
    border: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
    border-radius: 12px;
    box-shadow: 0 24px 60px -36px color-mix(in srgb, var(--zom-black) 90%, transparent);
    display: none;
    left: 24px;
    max-height: min(70vh, 420px);
    overflow-y: auto;
    padding: 8px;
    position: absolute;
    right: 24px;
    top: calc(100% + 6px);
    z-index: 2;
  }

  .zmk-sec-toc.is-open .zmk-sec-toc__list {
    display: flex;
  }

  .zmk-sec-toc__list a {
    border-radius: 8px;
    font-size: 16px;
    padding: 12px 12px;
  }

  .zmk-sec-toc__list a.is-active {
    background: color-mix(in srgb, var(--zmk-ink) 6%, transparent);
  }

  .zmk-sec-hero__inner {
    padding-bottom: 60px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 128px;
  }

  .zmk-sec-hero h1 {
    font-size: clamp(32px, 8.8vw, 42px);
  }

  .zmk-sec-lede {
    color: color-mix(in srgb, var(--zmk-ink) 78%, transparent);
    font-size: 17px;
    font-weight: 400;
  }

  .zmk-sec-content {
    gap: 72px;
  }

  .zmk-sec-document-kicker {
    font-size: 12px;
  }

  .zmk-sec-overview > p,
  .zmk-sec-domain__description,
  .zmk-sec-vdp__description {
    color: color-mix(in srgb, var(--zmk-ink) 80%, transparent);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.65;
  }

  .zmk-sec-card {
    padding: 24px 22px;
  }

  .zmk-sec-card h3,
  .zmk-sec-governance-card h3,
  .zmk-sec-governance-trust h3 {
    font-size: 18px;
    line-height: 1.35;
  }

  .zmk-sec-card p,
  .zmk-sec-governance-trust p,
  .zmk-sec-governance-card p,
  .zmk-sec-vdp-card p,
  .zmk-sec-list li,
  .zmk-sec-vdp-card--rewards .zmk-sec-list li,
  .zmk-sec-response__item > span:last-child {
    color: color-mix(in srgb, var(--zmk-ink) 82%, transparent);
    font-size: 16.5px;
    font-weight: 400;
    line-height: 1.6;
  }

  .zmk-sec-faq__toggle > span:first-child {
    font-size: 17px;
  }

  .zmk-sec-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }

  .zmk-sec-button,
  .zmk-sec-outline-button {
    justify-content: center;
    width: 100%;
  }

  .zmk-sec-card-grid,
  .zmk-sec-governance-grid,
  .zmk-sec-vdp-grid,
  .zmk-sec-vdp-grid--scope {
    grid-template-columns: minmax(0, 1fr);
  }

  .zmk-sec-regulatory__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 0;
  }

  .zmk-sec-regulatory__row dt,
  .zmk-sec-regulatory__row dd {
    white-space: normal;
  }

  .zmk-sec-footer__inner {
    padding: 56px 24px 40px;
  }

  .zmk-sec-footer__grid {
    gap: 44px;
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 48px;
  }

  .zmk-sec-footer__links {
    flex-wrap: wrap;
    gap: 40px 48px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .zmk-security-root *,
  .zmk-security-root *::before,
  .zmk-security-root *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.001ms !important;
  }

  .zmk-security-root [data-sreveal] {
    opacity: 1;
    transform: none;
  }
}
`;
